import { useState, useEffect } from "react";
import { useFilterContext } from "./ContextManager";
import "../css/cssVariables.css";
import style from "../css/summaryModal.module.css";

// Components
import { TabLabel } from "./SubComponents/TabLabel/TabLabel";
import { TopAirportsList } from "./SubComponents/TopAirportsList/TopAirportsList";
import { TopCountriesList } from "./SubComponents/TopCountriesList/TopCountriesList";
import { TopRoutesSavedList } from "./SubComponents/TopRoutesSavedList/TopRoutesSavedList";

// MUI
import {
  Tabs,
  Tab,
  Card,
  CardContent,
  Typography,
  Box,
  Modal,
  IconButton,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import leafIcon from "../assets/svg/eco_leaf.svg";
import timerIcon from "../assets/svg/timer.svg";

import { capitalizeFirstLetter, formatNumber } from "../utils/utilityFunctions";
import { totalPsoCounts, existingRoutesCount } from "../utils/psoCounts";
// Recharts
import {
  PieChart,
  Pie,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

// palette for the chart slices
const COLORS = [
  "#8884d8",
  "#82ca9d",
  "#ffc658",
  "#ff7f50",
  "#8dd1e1",
  "#a4de6c",
  "#d0ed57",
  "#83a6ed",
];

export function SummaryModal() {
  const {
    filters,
    filteredRoutesResult,
    airportRouteCountMap,
    airportIdMap,
    countrySummary,
    summaryOpen,
    updateSummaryOpen,
  } = useFilterContext();

  const [tabs, setTabs] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const handleClose = () => updateSummaryOpen(false);

  useEffect(() => {
    // drop any custom country (iso === "XX" or summary.type === "custom")
    const keys = Object.entries(countrySummary)
      .filter(([iso, summary]) => iso !== "XX" && summary.type !== "custom")
      .map(([iso]) => iso);
    const ordered = keys.includes("total")
      ? ["total", ...keys.filter((k) => k !== "total")]
      : keys;
    setTabs(ordered);
  }, [countrySummary]);

  const handleChange = (event, newValue) => {
    setActiveIndex(newValue);
  };

  // 1) strip out any user-made “custom” routes up front
  const realRoutes = filteredRoutesResult.filter(
    (r) => r.type !== "custom" && !r.countries.includes("XX")
  );

  // 2) strip out any user-made airports up front
  //    and rebuild a slimmer map without the custom ones
  const realAirportRouteCountMap = Object.fromEntries(
    Object.entries(airportRouteCountMap).filter(([id, count]) => {
      const ap = airportIdMap.get(id);
      return ap && ap.type !== "custom" && ap.country_iso !== "XX";
    })
  );

  const curISO = tabs[activeIndex];
  const cur = countrySummary[curISO] || {};

  // Filter routes for current country (or all for total)
  const routesForCur =
    curISO === "total"
      ? realRoutes
      : realRoutes.filter((r) => r.countries.includes(curISO));

  const routesWithPax = routesForCur.filter((r) => typeof r.pax === "number");

  // Pax Count
  const curPaxCount = () => {
    let domestic = 0,
      international = 0;

    routesWithPax.forEach((route) => {
      route.type === "domestic"
        ? (domestic = domestic + route.pax)
        : (international = international + route.pax);
    });
    if (curISO === "total") {
      international = international / 2;
    }
    return domestic + international;
  };

  // only when time-saved mode is on and car/transit is selected.
  const showTimeSavedChart = filters.compareRoutes;

  let timeSavedChartData = [];
  if (showTimeSavedChart) {
    // grab all numeric timeSaved values in minutes
    const minutesSaved = routesForCur
      .map((r) => r.timeSaved)
      .filter((m) => typeof m === "number");

    // figure out the highest hour bucket needed
    // const hourIndices = minutesSaved.map((m) => Math.ceil(m / 60));

    const hourIndices = minutesSaved
      .map((m) => Math.ceil(m / 60))
      .filter((h) => Number.isFinite(h) && h > 0);
    const maxHour = hourIndices.length ? Math.max(...hourIndices) : 0;

    // init counts to zero for 1h,2h,...,maxHour h
    const counts = Array.from({ length: maxHour }, () => 0);
    hourIndices.forEach((h) => {
      // h will be 1,2,3,...
      counts[h - 1]++;
    });

    // build the chart data
    timeSavedChartData = counts.map((count, i) => ({
      hour: `${i + 1}h`,
      count,
    }));
  }

  // // --- Average time‐saved calculation (min display as h or m) ---
  // let formattedAvgTimeSaved = "";
  // let timeSavedCount = 0;
  // if (showTimeSavedChart) {
  //   const mins = routesForCur
  //     .map((r) => r.timeSaved)
  //     .filter((m) => typeof m === "number");
  //   timeSavedCount = mins.length;
  //   const avgMin =
  //     timeSavedCount > 0
  //       ? mins.reduce((sum, m) => sum + m, 0) / timeSavedCount
  //       : 0;
  //   formattedAvgTimeSaved =
  //     avgMin >= 60
  //       ? `${(avgMin / 60).toFixed(1)} h`
  //       : `${Math.round(avgMin)} m`;
  // }

  // ---Top Routes By Time Saved---
  const TOP_ROUTES_LIMIT = 5;

  const topRoutesByTimeSaved = showTimeSavedChart
    ? routesForCur
        .filter((r) => Number.isFinite(r.timeSaved) && r.timeSaved > 0)
        .sort((a, b) => b.timeSaved - a.timeSaved)
        .slice(0, TOP_ROUTES_LIMIT)
        .map((r) => {
          const a1 = airportIdMap.get(r.airport_1);
          const a2 = airportIdMap.get(r.airport_2);
          const left = a1?.iata || a1?.name || r.airport_1;
          const right = a2?.iata || a2?.name || r.airport_2;
          return {
            id: r._id,
            name: r.name || `${left} → ${right}`,
            minutes: r.timeSaved,
          };
        })
    : [];

  // --- Adjust the total routes to account for international routes being counted twice (this is a quick fix that should be handled differently) ---
  const adjustedInternationalCount =
    curISO === "total"
      ? (cur.internationalRoutes || 0) / 2
      : cur.internationalRoutes || 0;

  const displayedRouteCount =
    curISO === "total"
      ? (cur.domesticRoutes || 0) + adjustedInternationalCount
      : cur.routeCount || 0;

  function getRelevantISOs(tabs, filters) {
    if (filters.country?.length) {
      return filters.country;
    }
    return tabs.filter((iso) => iso !== "total");
  }

  function getCountForISO(countsMap, isoKey, tabs, filters) {
    if (isoKey === "total") {
      const isosToSum = getRelevantISOs(tabs, filters);
      return isosToSum.reduce((sum, iso) => sum + (countsMap[iso] || 0), 0);
    }
    return countsMap[isoKey] || 0;
  }

  // utils (local)
  const median = (values) => {
    const xs = values.filter((v) => Number.isFinite(v)).sort((a, b) => a - b);
    const n = xs.length;
    if (!n) return null;
    const mid = Math.floor(n / 2);
    return n % 2 ? xs[mid] : (xs[mid - 1] + xs[mid]) / 2;
  };

  // ----- Route coverage calculation -----
  // PSO
  const totalPsoForCur = getCountForISO(totalPsoCounts, curISO, tabs, filters);
  const coveredPsoCount = routesForCur.filter((r) => r.pso).length;
  const psoCoveragePercent = totalPsoForCur
    ? (coveredPsoCount / totalPsoForCur) * 100
    : 0;

  // Existing‐routes
  const totalExistingForCur = getCountForISO(
    existingRoutesCount,
    curISO,
    tabs,
    filters
  );
  const coveredExistingCount = routesForCur.filter(
    (r) => r.commercial || r.pso
  ).length;
  const existingCoveragePercent = totalExistingForCur
    ? (coveredExistingCount / totalExistingForCur) * 100
    : 0;
  // ------------------------------------
  // Median distance across ALL routes for this tab
  const medianRouteDistance = (() => {
    const dists = routesForCur.map((r) => r.distance);
    return median(dists);
  })();

  // Median distance for EXISTING (commercial) routes for this tab
  const medianCommercialDistance = (() => {
    const dists = routesForCur
      .filter((r) => r.commercial)
      .map((r) => r.distance);
    return median(dists);
  })();

  // Average commercial distance for this tab
  const averageCommercialDistance = (() => {
    const com = routesForCur.filter((r) => r.commercial);
    if (!com.length) return null;
    return com.reduce((s, r) => s + r.distance, 0) / com.length;
  })();

  // Top 5 routes by pax for this tab
  const topRoutesByPax = routesForCur
    .filter((r) => typeof r.pax === "number")
    .sort((a, b) => b.pax - a.pax)
    .slice(0, 5);

  // --- CO₂ savings calculations (per tab) ---
  // 1) Average per‐passenger savings:
  const perPaxDiffs = routesForCur
    .map((r) => {
      const atr = r.ATR42_CO2?.CO2_perPassenger ?? 0;
      const es30 = r.ES30_CO2?.CO2_perPassenger ?? 0;
      return atr - es30;
    })
    // ignore any zero or negative diffs to only get true savings
    .filter((diff) => diff > 0);

  const averageEmissionSavedPerPax = perPaxDiffs.length
    ? perPaxDiffs.reduce((sum, d) => sum + d, 0) / perPaxDiffs.length
    : 0;

  // 2) Total annual savings (kg) across all pax‐counted routes:
  const totalEmissionSavedKg = routesWithPax.reduce((sum, r) => {
    const atr = r.ATR42_CO2?.CO2_perPassenger ?? 0;
    const es30 = r.ES30_CO2?.CO2_perPassenger ?? 0;
    const perPaxSaved = atr - es30;
    return sum + perPaxSaved * r.pax;
  }, 0);

  const formattedTotalEmissionSaved = (() => {
    const kg = totalEmissionSavedKg;
    if (kg >= 1_000) {
      // show in metric tons, one decimal
      const t = kg / 1_000;
      return `${Math.round(t).toLocaleString()} t`;
    } else {
      // stay in kilograms
      return `${Math.round(kg).toLocaleString()} kg`;
    }
  })();
  // ---------------------------------------------

  // Top 5 airports by connections for this tab
  const topAirports = Object.entries(realAirportRouteCountMap)
    .filter(([id, count]) => {
      const ap = airportIdMap.get(id);
      return ap && (curISO === "total" || ap.country_iso === curISO);
    })
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5)
    .map(([id, count]) => ({ name: airportIdMap.get(id).name, count }));

  // Chart data
  const airportTypesData = Object.entries(cur.airportTypes || {}).map(
    ([t, c]) => ({ name: capitalizeFirstLetter(t), value: c })
  );
  const routeTypeData = [
    { name: "Domestic", value: cur.domesticRoutes || 0 },
    { name: "International", value: adjustedInternationalCount },
  ].filter((d) => d.value > 0);

  // Total-only tables
  // Exclude the aggregated "total" row so it doesn't skew the country rankings
  const countryEntries = Object.entries(countrySummary)
    .filter(([iso, _]) => iso !== "total")
    .map(([, summary]) => summary);

  const barData = countryEntries
    .map((c) => ({ country: c.name, routes: c.routeCount }))
    .sort((a, b) => b.routes - a.routes)
    .slice(0, 5);

  const tableData = countryEntries
    .sort((a, b) => b.airportCount - a.airportCount)
    .slice(0, 5);

  return (
    <>
      {/* Trigger to open the modal */}
      <Modal
        open={summaryOpen}
        onClose={handleClose}
        aria-labelledby="summary-modal-title"
        slotProps={{ backdrop: { className: style.backdrop } }}
      >
        <Box
          className={style.summaryModal}
          sx={(theme) => ({
            // desktop / tablet (sm and up)
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            height: "97vh",
            width: "97vw",
            maxWidth: "1650px",
            maxHeight: "900px",
            p: 2,
            overflow: "hidden",

            // mobile: fill screen
            [theme.breakpoints.down("sm")]: {
              position: "fixed",
              inset: 0, // top:0, right:0, bottom:0, left:0
              transform: "none",
              width: "100vw",
              height: "100dvh",
              maxWidth: "100vw",
              maxHeight: "100dvh",
              borderRadius: 0,
              p: 1.5,
            },
          })}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
              gap: 2,
            }}
          >
            {/* Tabs — flex-grow so they take all available space */}
            <Tabs
              value={activeIndex}
              onChange={handleChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              textColor="primary"
              indicatorColor="primary"
              TabIndicatorProps={{
                style: { height: 4, backgroundColor: "var(--color-primary)" },
              }}
              sx={{
                flex: 1,
                minWidth: 0, // important so scroll works
                "& .MuiTab-root": {
                  minHeight: 56,
                  minWidth: 128,
                  fontSize: "1.1rem",
                  padding: "0 16px",
                },
              }}
            >
              {tabs.map((iso) => (
                <Tab
                  key={iso}
                  label={
                    <TabLabel
                      iso={iso}
                      name={countrySummary[iso]?.name || iso}
                    />
                  }
                  value={tabs.indexOf(iso)}
                  sx={{
                    fontWeight: 600,
                    fontSize: "1.4rem",
                    textTransform: "capitalize",
                  }}
                />
              ))}
            </Tabs>

            {/* Right-side disclaimer */}
            <Typography
              variant="caption"
              sx={{
                display: { xs: "none", sm: "inline-flex" }, // ← hide on xs
                color: "text.secondary",
                whiteSpace: "nowrap",
                fontWeight: 500,
                fontSize: "1.1rem",
                ml: 2,
                pr: "3rem",
                flexShrink: 0, // don't let it squeeze when visible
              }}
            >
              Summary based on current filters
            </Typography>
          </Box>

          {/* Close Button */}
          <IconButton
            aria-label="close summary"
            size="small"
            onClick={handleClose}
            sx={{ position: "absolute", top: 8, right: 8 }}
          >
            <CloseIcon />
          </IconButton>
          <div className={style.cardWrapper}>
            {" "}
            {/* KPI Row */}
            <div className={style.kpiRow}>
              <Card className={style.kpiCard}>
                <CardContent sx={{ padding: "0.5rem !important" }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 400, fontSize: "1.1rem" }}
                  >
                    Airports
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 600, fontSize: "2rem" }}
                  >
                    {cur.airportCount || 0}
                  </Typography>
                </CardContent>
              </Card>
              <Card className={style.kpiCard}>
                <CardContent sx={{ padding: "0.5rem !important" }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 400, fontSize: "1.1rem" }}
                  >
                    Routes
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 600, fontSize: "2rem" }}
                  >
                    {displayedRouteCount}
                  </Typography>
                </CardContent>
              </Card>

              <Card className={style.kpiCard}>
                <CardContent sx={{ padding: "0.5rem !important" }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 400, fontSize: "1.1rem" }}
                  >
                    PAX
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 600, fontSize: "2rem" }}
                  >
                    {formatNumber(curPaxCount())}
                  </Typography>
                </CardContent>
              </Card>

              <Card className={style.kpiCard}>
                <CardContent sx={{ padding: "0.5rem !important" }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 400, fontSize: "1.1rem" }}
                  >
                    Total Distance
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 600, fontSize: "2rem" }}
                  >
                    {cur.totalDistance
                      ? `${Math.round(cur.totalDistance).toLocaleString()} km`
                      : "–"}
                  </Typography>
                </CardContent>
              </Card>
              <Card className={style.kpiCard}>
                <CardContent sx={{ padding: "0.5rem !important" }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 400, fontSize: "1.1rem" }}
                  >
                    Median Route
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 600, fontSize: "2rem" }}
                  >
                    {medianRouteDistance != null
                      ? `${Math.round(medianRouteDistance)} km`
                      : "–"}
                  </Typography>
                </CardContent>
              </Card>

              <Card className={style.kpiCard}>
                <CardContent sx={{ padding: "0.5rem !important" }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 400, fontSize: "1.1rem" }}
                  >
                    Median Existing Route
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 600, fontSize: "2rem" }}
                  >
                    {medianCommercialDistance != null
                      ? `${Math.round(medianCommercialDistance)} km`
                      : "–"}
                  </Typography>
                </CardContent>
              </Card>

              {/* PSO Coverage */}
              <Card className={style.kpiCard}>
                <CardContent sx={{ padding: "0.5rem !important" }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 400, fontSize: "1.1rem" }}
                  >
                    PSO Coverage
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 600, fontSize: "2rem" }}
                  >
                    {totalPsoForCur > 0
                      ? `${coveredPsoCount}/${totalPsoForCur} (${Math.round(
                          psoCoveragePercent
                        )}%)`
                      : "–"}
                  </Typography>
                </CardContent>
              </Card>
              {/* Existing Routes Coverage */}
              <Card className={style.kpiCard}>
                <CardContent sx={{ padding: "0.5rem !important" }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 400, fontSize: "1.1rem" }}
                  >
                    Route Coverage
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 600, fontSize: "2rem" }}
                  >
                    {totalExistingForCur > 0
                      ? `${coveredExistingCount}/${totalExistingForCur} (${Math.round(
                          existingCoveragePercent
                        )}%)`
                      : "–"}
                  </Typography>
                </CardContent>
              </Card>
            </div>
            {/* CONTENT CARDS */}
            <div className={style.cardsGrid}>
              <Card>
                <CardContent className={style.cardContentFlexCenter}>
                  {/* 1) Average per‐pax CO₂ saved */}
                  <div>
                    {" "}
                    <Typography
                      variant="subtitle1"
                      sx={{ fontSize: "1.3rem", fontWeight: 500 }}
                    >
                      Avg. CO₂ Saved (per pax)
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        fontSize: "4rem",
                        fontWeight: 600,
                        lineHeight: "3rem",
                      }}
                    >
                      {`${averageEmissionSavedPerPax.toFixed(1)} kg`}
                    </Typography>
                  </div>
                  <img
                    src={leafIcon}
                    alt="green eco leaf icon"
                    className={style.leafIcon}
                  />
                  {/* 2) Total annual CO₂ saved */}
                  {/* Total Annual CO₂ Saved (kg/yr) */}
                  <div>
                    {" "}
                    <Typography
                      variant="subtitle1"
                      sx={{ fontSize: "1.3rem", fontWeight: 500 }}
                    >
                      Total CO₂ Saved (per year)
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        fontSize: "4rem",
                        fontWeight: 600,
                        lineHeight: "3rem",
                      }}
                    >
                      {formattedTotalEmissionSaved}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{
                        fontSize: "1rem",
                        fontWeight: 500,
                      }}
                    >
                      Based on {routesWithPax.length} routes with yearly
                      passenger data
                    </Typography>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent
                  className={`${style.cardContentFlexCenter} ${style.cardChart}`}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontSize: "1.3rem", fontWeight: 500 }}
                  >
                    Airport Types
                  </Typography>
                  {airportTypesData.length ? (
                    <ResponsiveContainer width="100%" height="85%">
                      <PieChart>
                        <Pie
                          data={airportTypesData}
                          dataKey="value"
                          nameKey="name"
                          innerRadius="45%"
                          outerRadius="75%"
                          paddingAngle={2}
                          label={({ name, value }) => `${value}`}
                          labelLine={false}
                        >
                          {airportTypesData.map((_, i) => (
                            <Cell key={i} fill={COLORS[i % COLORS.length]} />
                          ))}
                          {/* Title inside the chart */}
                        </Pie>
                        <Tooltip />
                        <Legend verticalAlign="bottom" height={36} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <Typography>No airport types</Typography>
                  )}
                </CardContent>
              </Card>
              {curISO === "total" && (
                <>
                  <Card>
                    <CardContent className={style.cardContentFlexCenter}>
                      {/* <Typography
                        variant="subtitle1"
                        sx={{ fontSize: "1.3rem", fontWeight: 500, mb: 1 }}
                      >
                        Airport Hubs by Country
                      </Typography> */}
                      <TopCountriesList
                        data={tableData.map((c) => ({
                          name: c.name,
                          iso: c.iso,
                          count: c.airportCount,
                        }))}
                      />
                    </CardContent>
                  </Card>
                </>
              )}

              <Card>
                <CardContent className={style.cardContentFlexCenter}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontSize: "1.3rem", fontWeight: 500 }}
                  >
                    Airports by Routes
                  </Typography>
                  <TopAirportsList topAirports={topAirports} />
                </CardContent>
              </Card>

              <Card>
                <CardContent className={style.cardContentFlexCenter}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontSize: "1.3rem", fontWeight: 500 }}
                  >
                    Route Distribution
                  </Typography>
                  {routeTypeData.length ? (
                    <ResponsiveContainer width="100%" height="85%">
                      <PieChart>
                        <Pie
                          data={routeTypeData}
                          dataKey="value"
                          nameKey="name"
                          innerRadius="45%"
                          outerRadius="75%"
                          paddingAngle={2}
                          label={({ name, value }) => `${value}`}
                          labelLine={false}
                        >
                          {routeTypeData.map((_, i) => (
                            <Cell key={i} fill={COLORS[i % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend verticalAlign="bottom" height={36} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <Typography>No route type data</Typography>
                  )}
                </CardContent>
              </Card>
              {/* <Card>
                <CardContent>
                  <Typography variant="subtitle1" style={{ marginTop: "1rem" }}>
                    Top Routes by Passenger Count
                  </Typography>
                  {topRoutesByPax.length ? (
                    <List dense>
                      {topRoutesByPax.map((r, i) => (
                        <ListItem key={i}>
                          {r.airport_1} → {r.airport_2}:{" "}
                          {r.pax.toLocaleString()} pax
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography>No passenger data</Typography>
                  )}
                </CardContent>
              </Card> */}
              {/* <Card>
                <CardContent>
                  <Typography variant="subtitle1" style={{ marginTop: "1rem" }}>
                    Top Airports by Connections
                  </Typography>
                  {topAirports.length ? (
                    <List dense>
                      {topAirports.map((a, i) => (
                        <ListItem key={i}>
                          {a.name}: {a.count}
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography>No connection data</Typography>
                  )}
                </CardContent>
              </Card> */}

              {curISO === "total" && (
                <>
                  <Card>
                    <CardContent className={style.cardContentFlexCenter}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontSize: "1.3rem", fontWeight: 500 }}
                      >
                        Routes per country
                      </Typography>
                      <ResponsiveContainer width="100%" height="85%">
                        <BarChart
                          data={barData}
                          margin={{ top: 10, right: 20, bottom: 0, left: -20 }}
                        >
                          <XAxis dataKey="country" />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="routes" name="Routes">
                            {barData.map((_, i) => (
                              <Cell key={i} fill={COLORS[i % COLORS.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* <Card>
                    <CardContent>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontSize: "1.3rem", fontWeight: 500 }}
                      >
                        Top Countries by Airport Count
                      </Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Country</TableCell>
                            <TableCell align="right">Airports</TableCell>
                            <TableCell align="right">Routes</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {tableData.map((c) => (
                            <TableRow key={c.iso}>
                              <TableCell>{c.name}</TableCell>
                              <TableCell align="right">
                                {c.airportCount}
                              </TableCell>
                              <TableCell align="right">
                                {c.routeCount}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card> */}
                </>
              )}

              {showTimeSavedChart && (
                <Card>
                  <CardContent className={style.cardContentFlexCenter}>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontSize: "1.3rem", fontWeight: 500 }}
                    >
                      How Many Routes Save X Hours
                    </Typography>
                    <ResponsiveContainer width="100%" height="85%">
                      <BarChart
                        data={timeSavedChartData}
                        margin={{ top: 10, right: 20, bottom: 0, left: -20 }}
                      >
                        <XAxis dataKey="hour" />
                        <YAxis allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey="count" name="Routes">
                          {timeSavedChartData.map((_, i) => (
                            <Cell key={i} fill={COLORS[i % COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* {showTimeSavedChart && (
                <Card>
                  <CardContent className={style.cardContentFlexCenter}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontSize: "1.5rem", fontWeight: 500 }}
                    >
                      Avg. Time Saved
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        flex: 1,
                        justifyContent: "center",
                        alignItems: "center",
                        width: "100%",
                        height: "100%",
                        gap: "1rem",
                      }}
                    >
                      {" "}
                      <img
                        src={timerIcon}
                        alt="timer clock icon"
                        className={style.timerIcon}
                      />
                      <Typography
                        variant="h4"
                        sx={{
                          fontSize: "6rem",
                          fontWeight: 600,
                          lineHeight: "4rem",
                        }}
                      >
                        {formattedAvgTimeSaved}
                      </Typography>
                    </Box>

                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{ fontSize: "1rem", fontWeight: 500 }}
                    >
                      Compared to car/public transport times on {timeSavedCount}{" "}
                      routes
                    </Typography>
                  </CardContent>
                </Card>
              )} */}
              {showTimeSavedChart && (
                <Card>
                  <CardContent className={style.cardContentFlexCenter}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontSize: "1.3rem", fontWeight: 500 }}
                    >
                      Routes by Time Saved
                    </Typography>
                    <TopRoutesSavedList items={topRoutesByTimeSaved} />
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </Box>
      </Modal>
    </>
  );
}
